import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { STATIC_PATH } from "../constants/config";
import Typography from "./Typography";
import { font } from "../constants/font";
import vmTheme from "../constants/theme";

/* ─────────────────────────────────────────────
   STYLED COMPONENTS
───────────────────────────────────────────── */

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${(p) => p.padding || "8px 14px"};
  background: ${(p) => (p.isOpen ? "#EDEEF2" : "#ffffff")};
  border-radius: ${(p) => p.borderRadius || "8px"};
  border: none;
  outline: none;
  font: ${font.normalBase_medium_16};
  color: #313345;
  transition: border-color 0.15s, background 0.15s;
  white-space: nowrap;
  user-select: none;
    * {
      touch-action: auto;
    }
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover {
    border-color: ${(p) => p.theme?.hoverBorder || "#94a3b8"};
    background: ${(p) => p.theme?.hoverBg || "#f8fafc"};
  }

  .trigger-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .trigger-label {
    flex: 1;
  }

  .chevron {
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
    transform: ${(p) => (p.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
    flex-shrink: 0;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12;
  touch-action: none;
`;

const MenuPanel = styled.div`
  position: fixed;
  z-index: 9999;
  top: ${(p) => p.top}px;
  left: ${(p) => p.left}px;
  width: ${(p) => p.width}px;
  background: #ffffff;
  border-radius: ${(p) => p.borderRadius || "10px"};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: ${(p) => p.menuPadding || "6px 0"};
  max-height: ${(p) => p.maxHeight || "380px"};
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
    * {
      touch-action: auto;
    }

  @media (max-width: 767px) {
    max-height: ${(p) => p.maxHeight || "min(260px, 50vh)"};
      * {
      touch-action: auto;
    }
    will-change: scroll-position;
    scroll-padding-bottom: 20px;
  }
`;

const SectionWrapper = styled.div`
    * {
      touch-action: auto;
    }

  &:not(:first-child) {
    border-top: 1px solid #d7dde0;
    padding-top: 6px;
  }
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${(p) => p.optionPadding || "8px 12px"};
  cursor: ${(p) => (p.isDisabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.isDisabled ? 0.45 : 1)};
  transition: background 0.1s;
  position: relative;
  touch-action: auto;
    * {
      touch-action: auto;
    }

  &:hover {
    background: ${(p) => (p.isDisabled ? "transparent" : "#f8fafc")};
  }

  ${(p) =>
        p.isSelected &&
        css`
      background: ${p.selectedBg || "#f0f7ff"};
      &:hover {
        background: ${p.selectedHoverBg || "#e8f2ff"};
      }
    `}

  .option-icon {
    width: ${(p) => p.iconSize || "20px"};
    height: ${(p) => p.iconSize || "20px"};
    object-fit: contain;
    flex-shrink: 0;
    border-radius: ${(p) => p.iconRadius || "4px"};
  }

  .option-text-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    overflow: hidden;
  }
`;

/* ─────────────────────────────────────────────
   INLINE SVG ICONS
───────────────────────────────────────────── */

const ChevronDownSVG = () => (
    <svg
        className="chevron"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="4 6 8 10 12 6" />
    </svg>
);

const CheckSVG = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M7.40064 13.269L4.36664 10.234C4.21539 10.0903 4.01399 10.0114 3.80538 10.014C3.59677 10.0167 3.39746 10.1008 3.24994 10.2483C3.10242 10.3958 3.01836 10.5951 3.01569 10.8037C3.01302 11.0123 3.09195 11.2137 3.23564 11.365L6.83564 14.965C6.98566 15.1149 7.18906 15.1991 7.40114 15.1991C7.61322 15.1991 7.81663 15.1149 7.96664 14.965L16.7666 6.16498C16.9103 6.01373 16.9893 5.81233 16.9866 5.60372C16.9839 5.39511 16.8999 5.1958 16.7523 5.04828C16.6048 4.90076 16.4055 4.8167 16.1969 4.81403C15.9883 4.81136 15.7869 4.89029 15.6356 5.03398L7.40064 13.269Z"
            fill="#00BAFF"
        />
    </svg>
);

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */

const GroupedDropdown = ({
    triggerLabel,
    triggerIcon,
    triggerIconAlt,
    sections,
    onSelect,
    selectedValues,
    width,
    menuPadding,
    maxHeight,
    borderRadius,
    iconSize,
    iconRadius,
    optionPadding,
    selectedColor,
    selectedBg,
    selectedHoverBg,
    checkColor,
    subtextColor,
    textColor,
    disabled,
}) => {
    const triggerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    // null until first computed — prevents top:0/left:0 flash
    const [panelPos, setPanelPos] = useState(null);
    const theme = useSelector((state) => state.app.get("theme"));

    const computePosition = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const panelWidth = width || Math.max(rect.width, 220);
            const left = Math.max(
                8,
                Math.min(rect.left - 32, window.innerWidth - panelWidth - 8)
            );
            setPanelPos({
                top: rect.bottom + 4,
                left,
                width: panelWidth,
            });
        }
    }, [width]);

    // Reposition on window resize while open
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleResize = () => computePosition();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isOpen, computePosition]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape" && isOpen) {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    const handleTriggerClick = useCallback(() => {
        if (disabled) return;
        if (!isOpen) {
            // Compute position BEFORE opening so first render is already correct
            computePosition();
        }
        setIsOpen((o) => !o);
    }, [disabled, isOpen, computePosition]);

    const handleOptionClick = useCallback(
        (section, option) => {
            if (option.isDisabled) return;
            onSelect?.(section.id, option);
            if (!option.isUpgrade) {
                setIsOpen(false);
            }
        },
        [onSelect]
    );

    const getSelectedValue = (sectionId) => selectedValues?.[sectionId];

    return (
        <>
            <TriggerButton
                ref={triggerRef}
                isOpen={isOpen}
                disabled={disabled}
                onClick={handleTriggerClick}
                borderRadius={borderRadius}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                {triggerIcon && (
                    <img
                        className="trigger-icon"
                        src={`${STATIC_PATH}${triggerIcon}`}
                        alt={triggerIconAlt || ""}
                        style={{ opacity: disabled ? 0.45 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                    />
                )}
                <Typography
                    innerContent={triggerLabel}
                    font={font.normalBase}
                    cursor={disabled ? "not-allowed" : "pointer"}
                    display="block"
                    textStyles={{
                        opacity: disabled ? 0.45 : 1,
                    }}
                />
                <ChevronDownSVG />
            </TriggerButton>

            {/* Only portal when open AND position is ready */}
            {isOpen &&
                panelPos &&
                createPortal(
                    <>
                        <Backdrop onClick={() => setIsOpen(false)} />
                        <MenuPanel
                            role="listbox"
                            top={panelPos.top}
                            left={panelPos.left}
                            width={panelPos.width}
                            menuPadding={menuPadding}
                            maxHeight={maxHeight}
                            borderRadius={borderRadius}
                            onTouchMove={(e) => e.stopPropagation()}
                        >
                            {sections.map((section) => (
                                <SectionWrapper key={section.id}>
                                    {section.title && (
                                        <Typography
                                            innerContent={section.title}
                                            color={vmTheme[theme].sbSecondaryColor}
                                            font={font.medium_12}
                                            display={"block"}
                                            padding={"8px 12px"}
                                        />
                                    )}
                                    {section.options.map((option, idx) => {
                                        const isSelected =
                                            String(getSelectedValue(section.id)) === String(option.value);
                                        return (
                                            <OptionRow
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${section.id}-${option.value}-${idx}`}
                                                role="option"
                                                aria-selected={isSelected}
                                                isSelected={isSelected}
                                                isDisabled={option.isDisabled}
                                                selectedColor={selectedColor}
                                                selectedBg={selectedBg}
                                                selectedHoverBg={selectedHoverBg}
                                                checkColor={checkColor}
                                                textColor={textColor}
                                                subtextColor={subtextColor}
                                                iconSize={iconSize}
                                                iconRadius={iconRadius}
                                                optionPadding={optionPadding}
                                                onClick={() => handleOptionClick(section, option)}
                                                title={option.tooltip || undefined}
                                            >
                                                {/* Left icon */}
                                                {option.icon && (
                                                    <img
                                                        className="option-icon"
                                                        src={`${STATIC_PATH}${isSelected && option.selectedIcon
                                                            ? option.selectedIcon
                                                            : option.icon
                                                            }`}
                                                        alt={option.iconAlt || option.label}
                                                    />
                                                )}

                                                {/* Label + subtext */}
                                                <div className="option-text-wrap">
                                                    <Typography
                                                        innerContent={option.label}
                                                        color={
                                                            isSelected
                                                                ? vmTheme[theme].secondaryBorderColor
                                                                : vmTheme[theme].tertiaryColor
                                                        }
                                                        font={font.normalMiniBase}
                                                        cursor={"pointer"}
                                                        padding={"2px 0px"}
                                                    />
                                                    {option.subtext && (
                                                        <Typography
                                                            innerContent={option.subtext}
                                                            color={vmTheme[theme].tertiaryColor}
                                                            font={font.normalMiniBase}
                                                            padding={"2px 0px"}
                                                        />
                                                    )}
                                                </div>

                                                {/* Right: upgrade icon OR checkmark */}
                                                {option.isUpgrade ? (
                                                    <img
                                                        className="option-icon"
                                                        src={option.upgradeIcon || ""}
                                                        alt="upgrade"
                                                        style={{ marginLeft: "auto" }}
                                                    />
                                                ) : (
                                                    isSelected && <CheckSVG color={checkColor} />
                                                )}
                                            </OptionRow>
                                        );
                                    })}
                                </SectionWrapper>
                            ))}
                        </MenuPanel>
                    </>,
                    document.body
                )}
        </>
    );
};

GroupedDropdown.propTypes = {
    triggerLabel: PropTypes.string.isRequired,
    triggerIcon: PropTypes.string,
    triggerIconAlt: PropTypes.string,
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string,
            headerColor: PropTypes.string,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    label: PropTypes.string.isRequired,
                    subtext: PropTypes.string,
                    icon: PropTypes.string,
                    selectedIcon: PropTypes.string,
                    iconAlt: PropTypes.string,
                    isDisabled: PropTypes.bool,
                    isUpgrade: PropTypes.bool,
                    upgradeIcon: PropTypes.string,
                    tooltip: PropTypes.string,
                })
            ).isRequired,
        })
    ).isRequired,
    onSelect: PropTypes.func,
    selectedValues: PropTypes.object,
    width: PropTypes.number,
    menuPadding: PropTypes.string,
    maxHeight: PropTypes.string,
    borderRadius: PropTypes.string,
    iconSize: PropTypes.string,
    iconRadius: PropTypes.string,
    optionPadding: PropTypes.string,
    selectedColor: PropTypes.string,
    selectedBg: PropTypes.string,
    selectedHoverBg: PropTypes.string,
    checkColor: PropTypes.string,
    subtextColor: PropTypes.string,
    textColor: PropTypes.string,
    disabled: PropTypes.bool,
};

export default GroupedDropdown;